import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SectionLoader, EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import {
  HardDrive, CheckCircle2, AlertTriangle, Upload, Trash2, Download, Copy,
  RefreshCw, Loader2, FileText, Image as ImageIcon,
} from "lucide-react";

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminStorage() {
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [st, fl] = await Promise.all([
      api.get("/storage/status").then((r) => r.data).catch(() => ({ configured: false, ok: false })),
      api.get("/storage/files").then((r) => r.data).catch(() => []),
    ]);
    setStatus(st); setFiles(fl); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "test");
      await api.post("/storage/upload", fd);
      toast.success("Uploaded to R2");
      await load();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (f) => {
    try { await api.delete(`/storage/${f.file_id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const copyUrl = (url) => {
    const abs = `${window.location.origin}${url}`;
    navigator.clipboard?.writeText(abs).then(() => toast.success("URL copied")).catch(() => {});
  };

  if (loading) return <SectionLoader />;

  const connected = status?.configured && status?.ok;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Storage</h1>
          <p className="mt-1 text-muted-foreground">Files stored on your Cloudflare R2 bucket.</p>
        </div>
        <Button variant="secondary" onClick={load} data-testid="storage-refresh-button"><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
      </div>

      {/* Status */}
      <Card className="border-border bg-card" data-testid="storage-status-card">
        <CardContent className="flex flex-wrap items-center gap-4 p-5">
          <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${connected ? "bg-[rgba(34,139,94,0.14)] text-[#1f7a52]" : "bg-[rgba(201,119,46,0.16)] text-[#8A4A18]"}`}>
            {connected ? <CheckCircle2 className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-foreground">{connected ? "Connected" : status?.configured ? "Configured — connection error" : "Not configured"}</p>
              {connected && <Badge variant="secondary">R2</Badge>}
            </div>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {status?.bucket ? <>Bucket <span className="font-medium text-foreground">{status.bucket}</span> · {status.files} file{status.files === 1 ? "" : "s"}</> : "Add R2 credentials to the backend env to enable storage."}
            </p>
            {status?.error && <p className="mt-0.5 text-xs text-destructive">{status.error}</p>}
          </div>
          <div className="ml-auto">
            <input id="storage-test-upload" type="file" className="hidden" onChange={onUpload} data-testid="storage-test-upload-input" />
            <Button asChild disabled={uploading || !connected} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]">
              <label htmlFor="storage-test-upload" className="cursor-pointer">{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{uploading ? "Uploading…" : "Upload a file"}</label>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Files */}
      {files.length === 0 ? (
        <EmptyState icon={HardDrive} title="No files yet" description="Files uploaded across the app (e.g. media photos) will appear here." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {files.map((f) => {
            const isImage = (f.content_type || "").startsWith("image/");
            return (
              <Card key={f.file_id} className="overflow-hidden border-border bg-card" data-testid="storage-file-card">
                <div className="flex h-36 items-center justify-center border-b border-border bg-secondary/40">
                  {isImage ? (
                    <img src={f.url} alt={f.original_filename || "file"} className="h-full w-full object-cover" />
                  ) : (
                    <FileText className="h-10 w-10 text-muted-foreground" />
                  )}
                </div>
                <CardContent className="p-4">
                  <p className="truncate text-sm font-medium text-foreground">{f.original_filename || f.key.split("/").pop()}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="font-normal">{f.prefix}</Badge>
                    <span>{formatBytes(f.size)}</span>
                    <span>· {formatDate(f.created_at)}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    <Button size="sm" variant="ghost" onClick={() => copyUrl(f.url)} data-testid="storage-copy-url"><Copy className="mr-1.5 h-3.5 w-3.5" />Copy URL</Button>
                    <Button asChild size="sm" variant="ghost"><a href={f.download_url} target="_blank" rel="noopener noreferrer"><Download className="mr-1.5 h-3.5 w-3.5" />Download</a></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="ml-auto text-destructive" data-testid="storage-delete-button"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete this file?</AlertDialogTitle><AlertDialogDescription>This permanently removes it from your R2 bucket.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(f)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
